<script lang="ts">
  import Table from "../../Table.svelte";
  import Dropdown from "../../Dropdown.svelte";
  import Button from "../../Button.svelte";
  import DateRange from "../../DateRange.svelte";
  import Pagination from "../../Pagination.svelte";
  import SearchInput from "../../SearchInput.svelte";
  import { afterUpdate, onMount } from "svelte";
  import { push } from "svelte-spa-router";
  import {
    PayoutService,
    PayoutStatusEnum,
    type DonationPayout,
    type PaginationInfo,
  } from "../../../lib/api/charityServices";

  // Filter states
  let selectedStatus = "";
  let search = "";
  let startDate = "";
  let endDate = "";
  let loading = false;
  let error: string | null = null;

  // Pagination
  let currentPage = 1;
  let paging: PaginationInfo | null = null;

  // Data
  let payouts: DonationPayout[] = [];

  // Dropdown options - Status options for upcoming (Pending only)
  let statusOptions = [
    { uuid: "3d8b53f5-11e2-4f8b-9d3b-bb86dcd1e640", name: "Pending" },
  ];

  // Fetch payouts data - only non-completed payouts
  async function fetchPayouts() {
    loading = true;
    error = null;

    try {
      const result = await PayoutService.getPayouts({
        status: selectedStatus,
        search: search,
        page: currentPage,
        per_page: 20,
        start_date: startDate,
        end_date: endDate,
      });

      if (result.success && result.payouts) {
        // Filter to only show non-completed payouts (pending or in progress)
        payouts = result.payouts.filter(p => {
          const status = p.donation_payout_status?.name || p.status?.name || '';
          return status.toLowerCase() !== 'completed';
        });
        
        // Sort by transaction_date or created_at, newest first
        payouts.sort((a, b) => {
          const dateA = new Date(a.transaction_date || a.created_at || 0).getTime();
          const dateB = new Date(b.transaction_date || b.created_at || 0).getTime();
          return dateB - dateA; // Descending order (newest first)
        });
        
        paging = result.paging || null;
      } else {
        error = result.error || "Failed to load upcoming payouts";
        payouts = [];
      }
    } catch (err) {
      console.error("Error fetching upcoming payouts:", err);
      error = "An error occurred while fetching upcoming payouts";
      payouts = [];
    } finally {
      loading = false;
    }
  }

  // Format date
  function formatDate(dateString: string | undefined): string {
    if (!dateString) return "";

    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    } catch (e) {
      return dateString;
    }
  }

  // Format currency
  function formatCurrency(amount: number | undefined): string {
    if (amount === undefined) return "";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  }

  // Table columns configuration
  const columns = [
    {
      key: "company",
      label: "Company",
      width: "35%",
      render: (value: string) => `<span class="company-name">${value}</span>`,
    },
    {
      key: "estimated_amount",
      label: "Estimated Amount",
      width: "25%",
      render: (value: string) => `<span class="estimated-amount">${value}</span>`,
    },
    {
      key: "status",
      label: "Status",
      width: "48%",
      align: 'center',
      render: (value: string) => {
        return `<div class="status-badge-wrapper" data-status="${value}"></div>`;
      },
    },
  ];

  // Transform payout data for table display
  function transformPayoutData(payouts: DonationPayout[]) {
    return payouts.map((payout) => {
      const status = payout.donation_payout_status?.name || payout.status?.name || "Pending";
      // Determine status display: if has employer_payment_uuid, it's "Sent to L2G", else "Pending"
      const displayStatus = payout.employer_payment_uuid ? "Sent to L2G" : "Pending";
      
      // Calculate estimated amount (96.5% of total, accounting for 3.5% L2G fee)
      const totalAmount = parseFloat(payout.amount || "0");
      const estimatedAmount = totalAmount * 0.965; // 96.5% = 100% - 3.5%
      
      return {
        payout_id: `#P-${payout.uuid?.substring(0, 5) || ""}`,
        company: payout.company || "N/A",
        estimated_amount: formatCurrency(estimatedAmount),
        total_donations: `${payout.total_donations || 0} donations`,
        status: displayStatus,
      };
    });
  }

  // Add status badges to rendered table
  afterUpdate(() => {
    const statusWrappers = document.querySelectorAll(".status-badge-wrapper");
    const transformedData = transformPayoutData(payouts);

    statusWrappers.forEach((wrapper, index) => {
      if (!wrapper.hasChildNodes() && transformedData[index]) {
        const payout = transformedData[index];
        const statusName = payout.status;
        const statusClass = getStatusClass(statusName);
        const statusIcon = getStatusIcon(statusName);

        wrapper.innerHTML = `<div class="status-badge ${statusClass}">${statusIcon}<span>${statusName}</span></div>`;
      }
    });

    // Add click event to rows
    const tableRows = document.querySelectorAll(".data-table tbody tr");
    tableRows.forEach((row, index) => {
      if (!row.hasAttribute("data-click-handler") && payouts[index]) {
        row.setAttribute("data-click-handler", "true");
        (row as HTMLElement).style.cursor = "pointer";

        row.addEventListener("click", () => {
          const payout = payouts[index];
          if (payout) {
            navigateToDetail(payout.uuid);
          }
        });
      }
    });
  });

  // Navigate to detail page
  function navigateToDetail(id: string) {
    push(`/upcoming-payout-detail/${id}`);
  }

  function getStatusClass(status: string): string {
    if (status === 'Sent to L2G') {
      return 'sent-to-l2g';
    }
    return 'pending';
  }

  function getStatusIcon(status: string): string {
    if (status === 'Sent to L2G') {
      return '<svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M12 4V1L8 5l4 4V6c3.31 0 6 2.69 6 6 0 1.01-.25 1.97-.7 2.8l1.46 1.46C19.54 15.03 20 13.57 20 12c0-4.42-3.58-8-8-8zm0 14c-3.31 0-6-2.69-6-6 0-1.01.25-1.97.7-2.8L5.24 7.74C4.46 8.97 4 10.43 4 12c0 4.42 3.58 8 8 8v3l4-4-4-4v3z"/></svg>';
    }
    return '<svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor"><path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8z"/><path d="M12.5 7H11v6l5.25 3.15.75-1.23-4.5-2.67z"/></svg>';
  }

  async function handleApply() {
    currentPage = 1;
    await fetchPayouts();
  }

  function handlePageChange(event: CustomEvent) {
    const page = event.detail.page;
    if (page !== currentPage) {
      currentPage = page;
      fetchPayouts();
    }
  }

  function onStatusChange(value: string) {
    selectedStatus = value;
  }

  function handleDateChange(event: CustomEvent) {
    startDate = event.detail.startDate;
    endDate = event.detail.endDate;
  }

  function handleCompanySearch() {
    handleApply();
  }

  // Reset all filters
  function resetFilters() {
    selectedStatus = "";
    search = "";
    startDate = "";
    endDate = "";
    fetchPayouts();
  }

  // Initialize data on component mount
  onMount(async () => {
    await fetchPayouts();
  });
</script>

<div class="payout-listing-container">
  <!-- Header -->
  <div class="page-header">
    <h1 class="page-title">Upcoming Payouts</h1>
  </div>

  <!-- Filters Section -->
  <div class="filters-section">
    <div class="filters-left">
      <SearchInput
        bind:value={search}
        placeholder="Search"
        on:input={handleCompanySearch}
        width="250px"
      />
      <Dropdown
        options={statusOptions}
        selectedValue={selectedStatus}
        placeholder="Status"
        onSelectionChange={onStatusChange}
      />

      <DateRange
        bind:startDate
        bind:endDate
        placeholder="Date Range"
        on:change={handleDateChange}
      />
    </div>

    <div class="filters-right">
      <Button variant="primary" on:click={handleApply} disabled={loading}>
        {loading ? "Loading..." : "Apply"}
      </Button>
    </div>
  </div>

  <!-- Table Section -->
  <div class="table-section">
    {#if loading && payouts.length === 0}
      <div class="loading-state">
        <div class="loading-spinner"></div>
        <p>Loading upcoming payouts...</p>
      </div>
    {:else if error}
      <div class="error-state">
        <p>{error}</p>
        <Button variant="primary" on:click={fetchPayouts}>Try Again</Button>
      </div>
    {:else if payouts.length === 0}
      <div class="empty-state">
        <div class="empty-icon">
          <svg viewBox="0 0 24 24" width="48" height="48" fill="#9CA3AF">
            <path
              d="M19 5v14H5V5h14m0-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-4.86 8.86l-3 3.87L9 13.14 6 17h12l-3.86-5.14z"
            />
          </svg>
        </div>
        <p class="empty-message">No upcoming payouts found</p>
        <Button variant="primary" on:click={resetFilters}>Reset Filters</Button>
      </div>
    {:else}
      <div class="table-container">
        {#if loading}
          <div class="table-loading-overlay">
            <div class="loading-spinner"></div>
          </div>
        {/if}
        <Table {columns} data={transformPayoutData(payouts)} />

        {#if paging && paging.last_page > 1}
          <div class="pagination-wrapper">
            <Pagination
              {currentPage}
              totalPages={paging.last_page}
              maxVisiblePages={7}
              on:pageChange={handlePageChange}
            />
          </div>
        {/if}
      </div>
    {/if}
  </div>
</div>

<style>
  .payout-listing-container {
    padding: 0;
    background-color: #f7fafc;
    min-height: calc(100vh - 70px);
  }

  .page-header {
    margin-bottom: 2rem;
  }

  .page-title {
    font-size: 1.875rem;
    font-weight: 700;
    color: #111827;
    margin: 0;
  }

  .filters-section {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 2rem;
    background: white;
    padding: 1.5rem;
    border-radius: 12px;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  }

  .filters-left {
    display: flex;
    gap: 1rem;
  }

  .filters-right {
    display: flex;
  }

  .table-section {
    background: white;
    border-radius: 12px;
    box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
    overflow: hidden;
    padding: 0 0 1rem 0;
  }

  .table-container {
    position: relative;
  }

  .table-loading-overlay {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(255, 255, 255, 0.7);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 10;
  }

  .loading-state,
  .error-state,
  .empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 3rem 1rem;
    text-align: center;
  }

  .empty-state {
    min-height: 300px;
    background-color: white;
  }

  .empty-icon {
    margin-bottom: 1rem;
    color: #9ca3af;
  }

  .empty-message {
    font-size: 1.125rem;
    color: #6b7280;
    margin-bottom: 1.5rem;
  }

  .loading-spinner {
    border: 3px solid #f3f3f3;
    border-top: 3px solid #f0b429;
    border-radius: 50%;
    width: 30px;
    height: 30px;
    animation: spin 1s linear infinite;
    margin-bottom: 1rem;
  }

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }

  .pagination-wrapper {
    margin-top: 1.5rem;
    padding: 0 1rem;
  }

  /* Global styles for table content */
  :global(.company-name) {
    color: #6b7280;
    font-weight: 500;
  }

  :global(.estimated-amount) {
    font-weight: 600;
    color: #111827;
  }

  :global(.status-badge-wrapper) {
    display: flex;
    align-items: center;
  }

  /* Status badge styles */
  :global(.status-badge) {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0.375rem 0.75rem;
    border-radius: 9999px;
    font-size: 0.875rem;
    font-weight: 500;
    line-height: 1;
    white-space: nowrap;
  }

  :global(.status-badge svg) {
    width: 16px;
    height: 16px;
    margin-right: 0.25rem;
    flex-shrink: 0;
    display: block;
  }

  :global(.status-badge span) {
    display: block;
    margin: 0;
    padding: 0;
  }

  :global(.status-badge.pending) {
    background-color: #fef3c7;
    color: #92400e;
  }

  :global(.status-badge.sent-to-l2g) {
    background-color: #dbeafe;
    color: #1e40af;
  }

  /* Responsive Design */
  @media (max-width: 1024px) {
    .filters-section {
      flex-direction: column;
      gap: 1rem;
    }

    .filters-left,
    .filters-right {
      width: 100%;
    }
  }

  @media (max-width: 768px) {
    .payout-listing-container {
      padding: 1rem;
    }

    .filters-left {
      flex-direction: column;
      width: 100%;
      gap: 0.75rem;
    }

    .filters-right {
      width: 100%;
    }
  }
</style>

